import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { cn } from '../utils/cn';

const CreateTournament = ({ session, supabase }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isPwa, setIsPwa] = useState(false);
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  
  // Tournament form state
  const [tournamentData, setTournamentData] = useState({
    name: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    open_registration: true,
  });
  
  // State for player search
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Check if running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsPwa(true);
    }

    // Check if user is logged in
    if (!session) {
      navigate('/');
      return;
    }

    // Fetch active players
    const fetchPlayers = async () => {
      setLoading(true);
      try {
        const { data: playersData, error: playersError } = await supabase
          .from('players')
          .select('id, username, user_id, elo, avatar_url')
          .eq('is_active', true)
          .order('username');

        if (playersError) throw playersError;

        const allPlayers = (playersData || []).map(player => ({
          ...player,
          source: player.user_id ? 'profile' : 'manual',
          elo: player.elo || 1000,
        })).sort((a, b) => a.username.localeCompare(b.username));

        setPlayers(allPlayers);
      } catch (err) {
        console.error('Error fetching players:', err.message);
        setError('Failed to load players. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, [supabase, session, navigate]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTournamentData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear messages on input change
    if (success) setSuccess(false);
    if (error) setError(null);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.trim()) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  // Handle selecting a player
  const handlePlayerSelect = (player) => {
    // Check if player is already selected
    if (!selectedPlayers.some(p => p.id === player.id)) {
      setSelectedPlayers([...selectedPlayers, player]);
    }
    
    // Clear search
    setSearchTerm('');
    setShowDropdown(false);
  };

  // Handle removing a player
  const handleRemovePlayer = (playerId) => {
    setSelectedPlayers(selectedPlayers.filter(p => p.id !== playerId));
  };

  // Navigate back
  const handleBack = () => {
    navigate('/tournaments');
  };

  // Filtered players for dropdown
  const filteredPlayers = players.filter(player => 
    player.username.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedPlayers.some(p => p.id === player.id)
  );

  // Add a new handler function for selecting all players
  const handleSelectAllPlayers = () => {
    // Filter out any already selected players
    const playersToAdd = players.filter(player => 
      !selectedPlayers.some(p => p.id === player.id)
    );
    
    // Add all remaining players to selection
    setSelectedPlayers([...selectedPlayers, ...playersToAdd]);
  };

  // Handle switch toggle for open registration
  const handleSwitchChange = (checked) => {
    setTournamentData(prev => ({
      ...prev,
      open_registration: checked
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!session) {
      setError('You must be logged in to create a tournament.');
      return;
    }

    if (!tournamentData.name) {
      setError('Tournament name is required.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Insert tournament
      const { data: tournamentData2, error: tournamentError } = await supabase
        .from('tournaments')
        .insert([
          {
            name: tournamentData.name,
            description: tournamentData.description,
            start_date: tournamentData.start_date,
            end_date: tournamentData.end_date || null,
            status: 'upcoming',
            created_by: session.user.id,
            player_count: selectedPlayers.length,
            open_registration: tournamentData.open_registration,
          }
        ])
        .select('id');
      
      if (tournamentError) throw tournamentError;
      
      const tournamentId = tournamentData2[0].id;
      
      // Insert tournament players if any are selected
      if (selectedPlayers.length > 0) {
        const tournamentPlayersData = selectedPlayers.map(player => ({
          tournament_id: tournamentId,
          player_id: player.id,
        }));
        
        const { error: playersError } = await supabase
          .from('tournament_players')
          .insert(tournamentPlayersData);
        
        if (playersError) throw playersError;
      }
      
      // Only generate initial bracket if not open registration and there are at least 2 players
      if (!tournamentData.open_registration && selectedPlayers.length >= 2) {
        // Generate initial tournament bracket
        const { error: matchesError } = await supabase.rpc('generate_tournament_bracket', {
          tournament_id: tournamentId,
        });
        
        if (matchesError) throw matchesError;
      }
      
      // Navigate to the tournament page
      navigate(`/tournament/${tournamentId}`);
      
    } catch (error) {
      console.error('Error creating tournament:', error.message);
      setError('Failed to create tournament. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className={cn(
      "flex flex-col w-full p-4 md:p-6 gap-6 pb-20",
      isPwa && "pt-12" // Add padding for PWA status bar
    )}>
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={handleBack}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Create Tournament</h1>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle>Tournament Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tournament Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Tournament Name</Label>
              <Input
                id="name"
                name="name"
                value={tournamentData.name}
                onChange={handleInputChange}
                placeholder="Enter tournament name"
                required
              />
            </div>

            {/* Tournament Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                name="description"
                value={tournamentData.description}
                onChange={handleInputChange}
                placeholder="Describe the tournament"
                rows={3}
              />
            </div>

            {/* Date Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={tournamentData.start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End Date (Optional)</Label>
                <Input
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={tournamentData.end_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Open Registration Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="open_registration"
                checked={tournamentData.open_registration}
                onCheckedChange={handleSwitchChange}
              />
              <Label htmlFor="open_registration">Open Registration</Label>
              <p className="text-sm text-muted-foreground ml-2">
                {tournamentData.open_registration 
                  ? "Players can sign up themselves" 
                  : "Only players you select can participate"}
              </p>
            </div>

            {/* Player Selection - Only show if open_registration is false */}
            {!tournamentData.open_registration && (
              <div className="space-y-4">
                <div className="flex flex-col">
                  <Label className="mb-2">Select Players</Label>
                  <div className="relative">
                    <Input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search for a player..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onClick={() => searchTerm && setShowDropdown(true)}
                    />
                    
                    {showDropdown && filteredPlayers.length > 0 && (
                      <div 
                        ref={dropdownRef}
                        className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg max-h-60 overflow-y-auto"
                      >
                        <div className="py-1">
                          {filteredPlayers.map(player => (
                            <div
                              key={player.id}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                              onClick={() => handlePlayerSelect(player)}
                            >
                              <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                                {player.username.charAt(0).toUpperCase()}
                              </div>
                              <span>{player.username}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-2 flex">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllPlayers}
                      className="ml-auto"
                    >
                      Add All Players
                    </Button>
                  </div>
                </div>
                
                {/* Selected Players */}
                <div className="mt-4">
                  <Label className="mb-2">Selected Players ({selectedPlayers.length})</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                    {selectedPlayers.map(player => (
                      <div 
                        key={player.id}
                        className="flex items-center justify-between gap-2 p-2 border rounded-md"
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                            {player.username.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium truncate">{player.username}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => handleRemovePlayer(player.id)}
                        >
                          &times;
                        </Button>
                      </div>
                    ))}
                    
                    {selectedPlayers.length === 0 && (
                      <div className="col-span-full p-8 text-center border rounded-md border-dashed bg-gray-50">
                        <p className="text-gray-500">No players selected yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md">
                {error}
              </div>
            )}
            
            {/* Success Message */}
            {success && (
              <div className="bg-green-50 text-green-600 p-3 rounded-md">
                Tournament created successfully!
              </div>
            )}
            
            {/* Submit Button */}
            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full sm:w-auto"
              >
                {submitting ? (
                  <>
                    <span className="mr-2">Creating...</span>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-opacity-50 border-t-transparent rounded-full"></div>
                  </>
                ) : 'Create Tournament'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTournament; 